import React, { useState, useEffect, useRef } from 'react';
import { useGridEnterNavigation } from '../hooks/useGridEnterNavigation';
import {
  Box, Button, Paper, IconButton, Typography, Chip, TextField,
  InputAdornment, CircularProgress, Stack, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, alpha, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon,
  CheckCircle as ApproveIcon, Cancel as RejectIcon,
  LocalPizza as BakeryIcon, Receipt as ReceiptIcon,
  HourglassTop as PendingIcon, ThumbUp as ThumbUpIcon,
  WarningAmber as WarningAmberIcon, Visibility as ViewIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { branchIssueRequestsService } from '../services/branchIssueRequests.service';
import { branchesService } from '../services/branches.service';
import { itemsService, itemUnitsService } from '../services/items.service';
import {
  BranchIssueRequestDto, CreateBranchIssueRequestDto,
  ApproveRequestDto, BranchIssueRequestDetailDto,
  UpdateBranchIssueRequestDto,
} from '../types/branchIssueRequests.types';
import { Branch } from '../types/branches.types';
import { Item } from '../types/items.types';
import BranchIssueRequestPreviewDialog from '../components/branchIssues/BranchIssueRequestPreviewDialog';

const ORANGE = '#e65100';
const GREEN = '#2e7d32';

const tr = {
  en: {
    title: 'Bakery Issue Requests', subtitle: 'Branch requests to bakery',
    addNew: 'New Request', search: 'Search...',
    reqNumber: 'Request #', date: 'Date', branch: 'Branch',
    items: 'Items', status: 'Status', issueRef: 'Issue Ref', actions: 'Actions',
    noData: 'No requests found',
    pending: 'Pending', approved: 'Approved', rejected: 'Rejected',
    totalReqs: 'Total', pendingCount: 'Pending', approvedCount: 'Approved',
    confirmDeleteTitle: 'Delete Request', confirmDeleteMsg: 'Delete this request?',
    confirmRejectTitle: 'Reject Request', confirmRejectMsg: 'Reject this request?',
    confirm: 'Confirm', cancel: 'Cancel',
    // Create dialog
    createTitle: 'New Issue Request', reqDate: 'Date', toBranch: 'Your Branch',
    notes: 'Notes', item: 'Item', unit: 'Unit', qty: 'Qty',
    addLine: 'Add Line', save: 'Submit Request',
    selectBranch: 'Select Branch', selectItem: 'Select Item',
    // Approve dialog
    approveTitle: 'Approve Request', approvedQty: 'Approved Qty',
    unitPrice: 'Sell Price', unitCost: 'Cost Price', approve: 'Approve & Create Issue',
    rejectionReason: 'Rejection Reason',
    // Edit dialog
    editTitle: 'Edit Request', saveEdit: 'Save Changes',
  },
  ar: {
    title: 'طلبات الإصدار من المخبز', subtitle: 'طلبات الفروع للمخبز',
    addNew: 'طلب جديد', search: 'بحث...',
    reqNumber: 'رقم الطلب', date: 'التاريخ', branch: 'الفرع',
    items: 'الأصناف', status: 'الحالة', issueRef: 'سند الإصدار', actions: 'الإجراءات',
    noData: 'لا توجد طلبات',
    pending: 'قيد الانتظار', approved: 'معتمد', rejected: 'مرفوض',
    totalReqs: 'الإجمالي', pendingCount: 'قيد الانتظار', approvedCount: 'معتمد',
    confirmDeleteTitle: 'حذف الطلب', confirmDeleteMsg: 'هل تريد حذف هذا الطلب؟',
    confirmRejectTitle: 'رفض الطلب', confirmRejectMsg: 'هل تريد رفض هذا الطلب؟',
    confirm: 'تأكيد', cancel: 'إلغاء',
    // Create dialog
    createTitle: 'طلب إصدار جديد', reqDate: 'التاريخ', toBranch: 'فرعك',
    notes: 'ملاحظات', item: 'الصنف', unit: 'الوحدة', qty: 'الكمية',
    addLine: 'إضافة سطر', save: 'إرسال الطلب',
    selectBranch: 'اختر الفرع', selectItem: 'اختر الصنف',
    // Approve dialog
    approveTitle: 'اعتماد الطلب', approvedQty: 'الكمية المعتمدة',
    unitPrice: 'سعر البيع', unitCost: 'سعر التكلفة', approve: 'اعتماد وإنشاء سند إصدار',
    rejectionReason: 'سبب الرفض',
    // Edit dialog
    editTitle: 'تعديل الطلب', saveEdit: 'حفظ التعديلات',
  },
};

interface ConfirmState { open: boolean; title: string; message: string; onConfirm: () => void; }
interface RequestLine { id: string; itemId: number | null; itemName: string; unitId: number | null; unitName: string; units: any[]; qty: number; notes: string; }
interface ApproveLine { detailId: number; itemName: string; unitName: string; requestedQty: number; approvedQty: number; unitPrice: number; }

const BranchIssueRequestsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = tr[language as 'en' | 'ar'];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  // Check user role for approve/edit permissions
  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canApproveRequest = userRoles.some(r => ['Super Admin', 'Admin', 'Warehouse Keeper', 'Warehouse Manager', 'Operations Manager'].includes(r));
  // Edit/Delete: Super Admin role OR explicit BRANCH_ISSUE_REQUEST_EDIT permission claim
  const userPerms = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const p = payload['Permission'];
      return p ? (Array.isArray(p) ? p : [p]) : [];
    } catch { return [] as string[]; }
  })();
  // Edit/Delete at ANY status: Super Admin, Warehouse Keeper, Warehouse Manager, Operations Manager, Admin, OR explicit permission
  const canEditOrDelete = userRoles.some(r => ['Super Admin', 'Warehouse Keeper', 'Warehouse Manager', 'Operations Manager', 'Admin'].includes(r)) || userPerms.includes('BRANCH_ISSUE_REQUEST_EDIT');

  const [requests, setRequests] = useState<BranchIssueRequestDto[]>([]);
  const [filtered, setFiltered] = useState<BranchIssueRequestDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Confirm dialog
  const [confirm, setConfirm] = useState<ConfirmState>({ open: false, title: '', message: '', onConfirm: () => {} });
  const closeConfirm = () => setConfirm(p => ({ ...p, open: false }));

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [branchId, setBranchId] = useState<number | null>(null);
  const [reqDate, setReqDate] = useState(new Date().toISOString().split('T')[0]);
  const [reqNotes, setReqNotes] = useState('');
  const [lines, setLines] = useState<RequestLine[]>([newLine()]);
  const [saving, setSaving] = useState(false);
  const [reqNumber, setReqNumber] = useState('');

  // Approve dialog
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveRequest, setApproveRequest] = useState<BranchIssueRequestDto | null>(null);
  const [approveLines, setApproveLines] = useState<ApproveLine[]>([]);
  const [approveNotes, setApproveNotes] = useState('');
  const [approving, setApproving] = useState(false);

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRequest, setRejectRequest] = useState<BranchIssueRequestDto | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editRequest, setEditRequest] = useState<BranchIssueRequestDto | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editLines, setEditLines] = useState<RequestLine[]>([newLine()]);
  const [editSaving, setEditSaving] = useState(false);

  // Preview dialog
  const [viewRequest, setViewRequest] = useState<BranchIssueRequestDto | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Enter-key navigation refs for each data-entry grid
  const createGridRef  = useRef<HTMLDivElement>(null);
  const editGridRef    = useRef<HTMLDivElement>(null);
  const approveGridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(createGridRef);
  useGridEnterNavigation(editGridRef);
  useGridEnterNavigation(approveGridRef);

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    const s = search.toLowerCase();
    setFiltered(requests.filter(r =>
      r.requestNumber.toLowerCase().includes(s) ||
      (r.branchName || '').toLowerCase().includes(s)
    ));
  }, [requests, search]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await branchIssueRequestsService.getAll();
      setRequests([...data].sort((a, b) => b.branchIssueRequestId - a.branchIssueRequestId));
    }
    finally { setLoading(false); }
  };

  function newLine(): RequestLine {
    return { id: Math.random().toString(), itemId: null, itemName: '', unitId: null, unitName: '', units: [], qty: 1, notes: '' };
  }

  const openCreate = async () => {
    const [allBranches, allItems, num] = await Promise.all([
      branchesService.getAll(),
      itemsService.getBakeryItems(),
      branchIssueRequestsService.generateNumber(),
    ]);
    setBranches(allBranches.filter((b: Branch) => b.branchType === 'Operational'));
    setItems(allItems);
    setReqNumber(num);
    setBranchId(null); setReqDate(new Date().toISOString().split('T')[0]);
    setReqNotes(''); setLines([newLine()]);
    setCreateOpen(true);
  };

  const handleItemChange = async (lineId: string, item: Item | null) => {
    if (!item) return;
    const unitList = await itemUnitsService.getItemUnits(item.itemId).catch(() => []);
    // Branch Issue Requests are by-definition for branch consumption, so only
    // units flagged isBranchUnit may be selected. Warehouse-only units (e.g.
    // bread "Bundle") are filtered out to prevent the silent bug that let a
    // branch manager pick Bundle for ITEM-000001..ITEM-000010 in BIR-2026-0354.
    const branchUnits = unitList.filter((u: any) => u.isBranchUnit === true);
    const units = branchUnits.map((u: any) => ({ unitId: u.unitId, unitName: u.unitName, unitNameAr: u.unitNameAr }));
    const def = units[0] || null;
    setLines(prev => prev.map(l => l.id === lineId ? {
      ...l, itemId: item.itemId,
      itemName: isRTL ? item.itemNameAr || item.itemName : item.itemName,
      units, unitId: def?.unitId ?? null,
      unitName: isRTL ? def?.unitNameAr || def?.unitName || '' : def?.unitName || '',
    } : l));
  };

  const handleCreate = async () => {
    if (!branchId) { toast.warning(isRTL ? 'اختر الفرع' : 'Select branch'); return; }
    if (lines.some(l => !l.itemId || !l.unitId || l.qty <= 0)) {
      toast.warning(isRTL ? 'تأكد من اكتمال جميع البنود' : 'Complete all lines'); return;
    }
    setSaving(true);
    try {
      const dto: CreateBranchIssueRequestDto = {
        requestNumber: reqNumber, requestDate: reqDate, branchId: branchId, notes: reqNotes,
        details: lines.map((l, i) => ({
          lineNumber: i + 1, itemId: l.itemId!, unitId: l.unitId!,
          requestedQuantity: l.qty, notes: l.notes || undefined,
        })),
      };
      await branchIssueRequestsService.create(dto);
      toast.success(isRTL ? 'تم إرسال الطلب بنجاح' : 'Request submitted successfully');
      setCreateOpen(false);
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    } finally { setSaving(false); }
  };

  const openEdit = async (req: BranchIssueRequestDto) => {
    if (!items.length) {
      const allItems = await itemsService.getBakeryItems();
      setItems(allItems);
    }
    setEditRequest(req);
    setEditDate(req.requestDate);
    setEditNotes(req.notes || '');
    const loadedLines: RequestLine[] = await Promise.all(
      req.details.map(async (d) => {
        const unitList = await itemUnitsService.getItemUnits(d.itemId).catch(() => []);
        // Same isBranchUnit filter as create mode. If the saved record has a
        // non-branch unit (legacy bug), the dropdown will only offer branch
        // units so the user must pick a valid one on next edit.
        const branchUnits = unitList.filter((u: any) => u.isBranchUnit === true);
        const units = branchUnits.map((u: any) => ({ unitId: u.unitId, unitName: u.unitName, unitNameAr: u.unitNameAr }));
        return {
          id: Math.random().toString(),
          itemId: d.itemId,
          itemName: isRTL ? d.itemNameAr || d.itemName : d.itemName,
          unitId: d.unitId,
          unitName: isRTL ? d.unitNameAr || d.unitName : d.unitName,
          units,
          qty: d.requestedQuantity,
          notes: d.notes || '',
        };
      })
    );
    setEditLines(loadedLines);
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editRequest) return;
    if (editLines.some(l => !l.itemId || !l.unitId || l.qty <= 0)) {
      toast.warning(isRTL ? 'تأكد من اكتمال جميع البنود' : 'Complete all lines'); return;
    }
    setEditSaving(true);
    try {
      const dto: UpdateBranchIssueRequestDto = {
        requestDate: editDate,
        notes: editNotes || undefined,
        details: editLines.map((l, i) => ({
          lineNumber: i + 1, itemId: l.itemId!, unitId: l.unitId!,
          requestedQuantity: l.qty, notes: l.notes || undefined,
        })),
      };
      await branchIssueRequestsService.update(editRequest.branchIssueRequestId, dto);
      toast.success(isRTL ? 'تم تعديل الطلب بنجاح' : 'Request updated successfully');
      setEditOpen(false);
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    } finally { setEditSaving(false); }
  };

  const openApprove = async (req: BranchIssueRequestDto) => {
    setApproveRequest(req);
    setApproveNotes('');
    // جلب وحدات كل صنف للحصول على السعر الافتراضي
    const lines = await Promise.all(req.details.map(async (d) => {
      let defaultPrice = d.unitPrice ?? 0;
      if (!defaultPrice) {
        const unitList = await itemUnitsService.getItemUnits(d.itemId).catch(() => []);
        const matchedUnit = unitList.find((u: any) => u.unitId === d.unitId);
        defaultPrice = matchedUnit?.defaultSalePrice ?? 0;
      }
      return {
        detailId: d.branchIssueRequestDetailId,
        itemName: isRTL ? d.itemNameAr || d.itemName : d.itemName,
        unitName: isRTL ? d.unitNameAr || d.unitName : d.unitName,
        requestedQty: d.requestedQuantity,
        approvedQty: d.requestedQuantity,
        unitPrice: defaultPrice,
      };
    }));
    setApproveLines(lines);
    setApproveOpen(true);
  };

  const handleApprove = async () => {
    if (!approveRequest) return;
    if (approveLines.some(l => l.approvedQty <= 0 || l.unitPrice <= 0)) {
      toast.warning(isRTL ? 'تأكد من الكمية وسعر البيع لكل بند' : 'Check quantity and sell price for all lines'); return;
    }
    setApproving(true);
    try {
      const dto: ApproveRequestDto = {
        notes: approveNotes,
        details: approveLines.map(l => ({
          branchIssueRequestDetailId: l.detailId,
          approvedQuantity: l.approvedQty,
          unitPrice: l.unitPrice,
          unitCost: 0,
        })),
      };
      await branchIssueRequestsService.approve(approveRequest.branchIssueRequestId, dto);
      toast.success(isRTL ? 'تم الاعتماد وإنشاء سند الإصدار كمسودة' : 'Approved — draft issue created');
      setApproveOpen(false);
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    } finally { setApproving(false); }
  };

  const handleReject = (req: BranchIssueRequestDto) => {
    setRejectRequest(req); setRejectReason(''); setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectRequest) return;
    try {
      await branchIssueRequestsService.reject(rejectRequest.branchIssueRequestId, rejectReason);
      toast.success(isRTL ? 'تم رفض الطلب' : 'Request rejected');
      setRejectOpen(false);
      await loadData();
    } catch (err: any) { toast.error(err.response?.data?.message || err.message); }
  };

  const handleDelete = (req: BranchIssueRequestDto) => {
    setConfirm({
      open: true, title: t.confirmDeleteTitle, message: t.confirmDeleteMsg,
      onConfirm: async () => {
        closeConfirm();
        try {
          await branchIssueRequestsService.delete(req.branchIssueRequestId);
          toast.success(isRTL ? 'تم الحذف' : 'Deleted');
          await loadData();
        } catch (err: any) { toast.error(err.response?.data?.message || err.message); }
      },
    });
  };

  const handleView = async (req: BranchIssueRequestDto) => {
    try {
      const full = await branchIssueRequestsService.getById(req.branchIssueRequestId);
      setViewRequest(full);
      setPreviewOpen(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const statusColor = (s: string) => s === 'Approved' ? 'success' : s === 'Rejected' ? 'error' : 'warning';
  const statusLabel = (s: string) => s === 'Approved' ? t.approved : s === 'Rejected' ? t.rejected : t.pending;

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'Pending').length,
    approved: requests.filter(r => r.status === 'Approved').length,
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: t.totalReqs, value: stats.total, icon: <ReceiptIcon />, color: ORANGE },
          { label: t.pendingCount, value: stats.pending, icon: <PendingIcon />, color: '#f57c00' },
          { label: t.approvedCount, value: stats.approved, icon: <ThumbUpIcon />, color: GREEN },
        ].map((s, i) => (
          <Card key={i} elevation={3} sx={{ flex: '1 1 180px', background: `linear-gradient(135deg, ${s.color} 0%, ${s.color}cc 100%)`, color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h5" fontWeight="bold">{s.value}</Typography>
                  <Typography variant="body2">{s.label}</Typography>
                </Box>
                <Box sx={{ opacity: 0.3, fontSize: 40 }}>{s.icon}</Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Header */}
      <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <BakeryIcon sx={{ color: ORANGE, fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color={ORANGE}>{t.title}</Typography>
              <Typography variant="caption" color="text.secondary">{t.subtitle}</Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadData} color="primary"><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
              sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#bf360c' } }}>
              {t.addNew}
            </Button>
          </Box>
        </Box>
        <TextField placeholder={t.search} value={search} onChange={e => setSearch(e.target.value)}
          size="small" sx={{ width: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: `linear-gradient(135deg, ${ORANGE} 0%, #ff8f00 100%)` }}>
              {[t.reqNumber, t.date, t.branch, t.items, t.status, t.issueRef, t.actions]
                .map(h => <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress sx={{ color: ORANGE }} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">{t.noData}</Typography>
              </TableCell></TableRow>
            ) : filtered.map(req => (
              <TableRow key={req.branchIssueRequestId} hover sx={{ '&:hover': { bgcolor: alpha(ORANGE, 0.04) } }}>
                <TableCell><Typography fontWeight="bold" color={ORANGE}>{req.requestNumber}</Typography></TableCell>
                <TableCell>{new Date(req.requestDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                <TableCell>{isRTL ? req.branchNameAr || req.branchName : req.branchName}</TableCell>
                <TableCell>{req.details.length}</TableCell>
                <TableCell>
                  <Chip label={statusLabel(req.status)} color={statusColor(req.status) as any}
                    size="small" sx={{ fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>
                  {req.issueNumber && (
                    <Chip label={req.issueNumber} size="small" variant="outlined" color="primary" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={isRTL ? 'معاينة' : 'Preview'}>
                      <IconButton size="small" color="info" onClick={() => handleView(req)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* Edit: visible on Pending for approvers, OR on any status for Super Admin / permission holders */}
                    {((req.status === 'Pending' && canApproveRequest) || canEditOrDelete) && (
                      <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
                        <IconButton size="small" color="warning" onClick={() => openEdit(req)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {req.status === 'Pending' && canApproveRequest && (
                      <>
                        <Tooltip title={isRTL ? 'اعتماد' : 'Approve'}>
                          <IconButton size="small" color="success" onClick={() => openApprove(req)}>
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isRTL ? 'رفض' : 'Reject'}>
                          <IconButton size="small" color="error" onClick={() => handleReject(req)}>
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {/* Delete: visible on Pending for approvers, OR on any status for Super Admin / permission holders */}
                    {((req.status === 'Pending' && canApproveRequest) || canEditOrDelete) && (
                      <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(req)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Create Dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ bgcolor: ORANGE, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <BakeryIcon /> {t.createTitle}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2} mt={1}>
            <TextField label={isRTL ? 'رقم الطلب' : 'Request #'} value={reqNumber} size="small" sx={{ width: 160 }} disabled />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.reqDate}
                value={reqDate ? new Date(reqDate) : null}
                onChange={(date) => setReqDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 160 } } }}
              />
            </LocalizationProvider>
            <Autocomplete options={branches}
              getOptionLabel={b => `${b.branchCode} - ${isRTL ? b.branchNameAr || b.branchName : b.branchName}`}
              value={branches.find(b => b.branchId === branchId) || null}
              onChange={(_, v) => setBranchId(v?.branchId ?? null)}
              renderInput={p => <TextField {...p} label={t.toBranch} size="small" />} sx={{ minWidth: 220 }} />
            <TextField label={t.notes} value={reqNotes} onChange={e => setReqNotes(e.target.value)}
              size="small" sx={{ minWidth: 180 }} />
          </Box>
          <Box ref={createGridRef}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(ORANGE, 0.08) }}>
                  <TableCell sx={{ width: 35 }}>#</TableCell>
                  <TableCell>{t.item}</TableCell>
                  <TableCell sx={{ width: 150 }}>{t.unit}</TableCell>
                  <TableCell sx={{ width: 100 }}>{t.qty}</TableCell>
                  <TableCell sx={{ width: 35 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, idx) => (
                  <TableRow key={line.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Autocomplete options={items}
                        getOptionLabel={i => isRTL ? i.itemNameAr || i.itemName : i.itemName}
                        value={items.find(i => i.itemId === line.itemId) || null}
                        onChange={(_, v) => handleItemChange(line.id, v)}
                        renderInput={p => <TextField {...p} size="small" placeholder={t.selectItem} />} size="small" />
                    </TableCell>
                    <TableCell>
                      <Autocomplete options={line.units}
                        getOptionLabel={(u: any) => isRTL ? u.unitNameAr || u.unitName : u.unitName}
                        value={line.units.find((u: any) => u.unitId === line.unitId) || null}
                        onChange={(_, v: any) => setLines(prev => prev.map(l => l.id === line.id ? { ...l, unitId: v?.unitId ?? null, unitName: isRTL ? v?.unitNameAr || v?.unitName || '' : v?.unitName || '' } : l))}
                        renderInput={p => <TextField {...p} size="small" />} size="small" />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={line.qty === 0 ? '' : String(line.qty)}
                        onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setLines(prev => prev.map(l => l.id === line.id ? { ...l, qty: v === '' || v === '.' ? 0 : parseFloat(v) } : l)); }}
                        inputProps={{ inputMode: 'decimal' }} sx={{ width: 90 }} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" disabled={lines.length === 1}
                        onClick={() => setLines(prev => prev.filter(l => l.id !== line.id))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          <Button startIcon={<AddIcon />} onClick={() => setLines(p => [...p, newLine()])} size="small" sx={{ mt: 1, color: ORANGE }}>
            {t.addLine}
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}
            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#bf360c' } }}>
            {saving ? <CircularProgress size={20} /> : t.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ bgcolor: '#f57c00', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon /> {t.editTitle} — {editRequest?.requestNumber}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2} mt={1}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.reqDate}
                value={editDate ? new Date(editDate) : null}
                onChange={(date) => setEditDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 160 } } }}
              />
            </LocalizationProvider>
            <TextField label={t.notes} value={editNotes} onChange={e => setEditNotes(e.target.value)}
              size="small" sx={{ minWidth: 200 }} />
          </Box>
          <Box ref={editGridRef}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#f57c00', 0.08) }}>
                  <TableCell sx={{ width: 35 }}>#</TableCell>
                  <TableCell>{t.item}</TableCell>
                  <TableCell sx={{ width: 150 }}>{t.unit}</TableCell>
                  <TableCell sx={{ width: 100 }}>{t.qty}</TableCell>
                  <TableCell sx={{ width: 35 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {editLines.map((line, idx) => (
                  <TableRow key={line.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Autocomplete options={items}
                        getOptionLabel={i => isRTL ? i.itemNameAr || i.itemName : i.itemName}
                        value={items.find(i => i.itemId === line.itemId) || null}
                        onChange={async (_, v) => {
                          if (!v) return;
                          const unitList = await itemUnitsService.getItemUnits(v.itemId).catch(() => []);
                          // Filter out warehouse-only units (isBranchUnit=false)
                          // — branches can only request units flagged for branches.
                          const branchUnits = unitList.filter((u: any) => u.isBranchUnit === true);
                          const units = branchUnits.map((u: any) => ({ unitId: u.unitId, unitName: u.unitName, unitNameAr: u.unitNameAr }));
                          const def = units[0] || null;
                          setEditLines(prev => prev.map(l => l.id === line.id ? {
                            ...l, itemId: v.itemId,
                            itemName: isRTL ? v.itemNameAr || v.itemName : v.itemName,
                            units, unitId: def?.unitId ?? null,
                            unitName: isRTL ? def?.unitNameAr || def?.unitName || '' : def?.unitName || '',
                          } : l));
                        }}
                        renderInput={p => <TextField {...p} size="small" placeholder={t.selectItem} />} size="small" />
                    </TableCell>
                    <TableCell>
                      <Autocomplete options={line.units}
                        getOptionLabel={(u: any) => isRTL ? u.unitNameAr || u.unitName : u.unitName}
                        value={line.units.find((u: any) => u.unitId === line.unitId) || null}
                        onChange={(_, v: any) => setEditLines(prev => prev.map(l => l.id === line.id ? { ...l, unitId: v?.unitId ?? null, unitName: isRTL ? v?.unitNameAr || v?.unitName || '' : v?.unitName || '' } : l))}
                        renderInput={p => <TextField {...p} size="small" />} size="small" />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={line.qty === 0 ? '' : String(line.qty)}
                        onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setEditLines(prev => prev.map(l => l.id === line.id ? { ...l, qty: v === '' || v === '.' ? 0 : parseFloat(v) } : l)); }}
                        inputProps={{ inputMode: 'decimal' }} sx={{ width: 90 }} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" disabled={editLines.length === 1}
                        onClick={() => setEditLines(prev => prev.filter(l => l.id !== line.id))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          <Button startIcon={<AddIcon />} onClick={() => setEditLines(p => [...p, newLine()])} size="small" sx={{ mt: 1, color: '#f57c00' }}>
            {t.addLine}
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={editSaving}
            sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#e65100' } }}>
            {editSaving ? <CircularProgress size={20} /> : t.saveEdit}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Approve Dialog ── */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ bgcolor: GREEN, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApproveIcon /> {t.approveTitle} — {approveRequest?.requestNumber}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box ref={approveGridRef}>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(GREEN, 0.08) }}>
                  <TableCell>{t.item}</TableCell>
                  <TableCell>{t.unit}</TableCell>
                  <TableCell>{isRTL ? 'الكمية المطلوبة' : 'Requested'}</TableCell>
                  <TableCell>{t.approvedQty}</TableCell>
                  <TableCell>{t.unitPrice}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approveLines.map((l, i) => (
                  <TableRow key={l.detailId}>
                    <TableCell><Typography fontWeight="bold">{l.itemName}</Typography></TableCell>
                    <TableCell>{l.unitName}</TableCell>
                    <TableCell><Typography color="text.secondary">{l.requestedQty.toFixed(3)}</Typography></TableCell>
                    <TableCell>
                      <TextField size="small" value={l.approvedQty === 0 ? '' : String(l.approvedQty)}
                        onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setApproveLines(prev => prev.map((x, j) => j === i ? { ...x, approvedQty: v === '' || v === '.' ? 0 : parseFloat(v) } : x)); }}
                        inputProps={{ inputMode: 'decimal' }} sx={{ width: 100 }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={l.unitPrice === 0 ? '' : String(l.unitPrice)}
                        onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setApproveLines(prev => prev.map((x, j) => j === i ? { ...x, unitPrice: v === '' || v === '.' ? 0 : parseFloat(v) } : x)); }}
                        inputProps={{ inputMode: 'decimal' }} sx={{ width: 110 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          <TextField label={t.notes} value={approveNotes} onChange={e => setApproveNotes(e.target.value)}
            size="small" fullWidth multiline rows={2} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApproveOpen(false)} disabled={approving}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleApprove} disabled={approving}
            sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#1b5e20' } }}>
            {approving ? <CircularProgress size={20} /> : t.approve}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject Dialog ── */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <Box sx={{ textAlign: 'center', pt: 4, pb: 1, px: 3 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
            background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(211,47,47,0.35)' }}>
            <WarningAmberIcon sx={{ fontSize: 34, color: '#fff' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{t.confirmRejectTitle}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t.confirmRejectMsg}</Typography>
          <TextField label={t.rejectionReason} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            size="small" fullWidth sx={{ mb: 1 }} />
        </Box>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button onClick={() => setRejectOpen(false)} variant="outlined">{t.cancel}</Button>
          <Button onClick={confirmReject} variant="contained" color="error">{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Preview Dialog ── */}
      <BranchIssueRequestPreviewDialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setViewRequest(null); }}
        request={viewRequest}
      />

      {/* ── Confirm Dialog ── */}
      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }} dir={isRTL ? 'rtl' : 'ltr'}>
        <Box sx={{ textAlign: 'center', pt: 4, pb: 1, px: 3 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
            background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(211,47,47,0.35)' }}>
            <WarningAmberIcon sx={{ fontSize: 34, color: '#fff' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{confirm.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{confirm.message}</Typography>
        </Box>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button onClick={closeConfirm} variant="outlined">{t.cancel}</Button>
          <Button onClick={confirm.onConfirm} variant="contained" color="error">{t.confirm}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchIssueRequestsPage;
